import { useEffect, useRef, useState } from "react";
import "./DidAvatarCode.css";
import {
  createAgentStream,
  submitSdpAnswer,
  submitIceCandidate,
  sendTextContent,
  sendAudioContent,
} from "./BAckendAPI";
import IdleVideo from "../../images/AvatarImages/Orbiee_3_video (2).mp4";

export default function DidAvatarCode() {
  const streamVideoRef = useRef(null);
  const idleVideoRef = useRef(null);
  const textAreaRef = useRef(null);
  const previewNameRef = useRef(null);
  const connectionLabelRef = useRef(null);
  const answersRef = useRef(null);
  const hiddenRef = useRef(null);
  const hiddenH2Ref = useRef(null);
  const connectButtonRef = useRef(null);

  const peerConnectionRef = useRef(null);
  const streamIdRef = useRef(null);
  const isConnectingRef = useRef(false);

  const [selectedOption, setSelectedOption] = useState("chat");
  const [isInterruptVisible, setIsInterruptVisible] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected"); // disconnected, connecting, connected
  const [chatHistory, setChatHistory] = useState([]);
  const [currentStreamId, setCurrentStreamId] = useState(null);

  useEffect(() => {
    // Helper to check if a file exists before loading
    const checkFileExists = async (url) => {
      try {
        const response = await fetch(url, { method: "HEAD" });
        return response.ok;
      } catch {
        return false;
      }
    };

    // Load external scripts with error handling
    const loadScript = async (src, type = "text/javascript") => {
      try {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          return;
        }

        // Check if file exists first
        const exists = await checkFileExists(src);
        if (!exists) {
          console.warn(`Script not found: ${src}. Skipping load.`);
          return;
        }

        return new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = src;
          script.type = type;
          script.onload = () => {
           
            resolve();
          };
          script.onerror = (error) => {
            console.warn(`Failed to load script: ${src}`, error);
            // Remove the script element on error to prevent issues
            if (script.parentNode) {
              script.parentNode.removeChild(script);
            }
            reject(error);
          };
          document.body.appendChild(script);
        });
      } catch (error) {
        console.warn(`Error loading script ${src}:`, error);
      }
    };

    // Load scripts only if they exist
    const loadScripts = async () => {
      const agentsScriptPath = "/agents-streams-api.js";
      const webSpeechPath = "/webSpeechAPI.js";

      // Check and load agents-streams-api.js
      const agentsExists = await checkFileExists(agentsScriptPath);
      if (agentsExists) {
        try {
          const agentsScript = document.createElement("script");
          agentsScript.type = "module";
          agentsScript.src = agentsScriptPath;
          agentsScript.onerror = () => {
            console.warn(`Failed to execute agents-streams-api.js`);
            if (agentsScript.parentNode) {
              agentsScript.parentNode.removeChild(agentsScript);
            }
          };
          agentsScript.onload = () => {
            console.log("Successfully loaded agents-streams-api.js");
          };
          document.body.appendChild(agentsScript);
        } catch (error) {
          console.warn("Error loading agents-streams-api.js:", error);
        }
      } else {
        console.info("agents-streams-api.js not found. Component will work without it.");
      }

      // Check and load webSpeechAPI.js
      await loadScript(webSpeechPath);
    };

    loadScripts();

    return () => {
      // Cleanup scripts on unmount
      const scripts = document.querySelectorAll(
        'script[src="/agents-streams-api.js"], script[src="/webSpeechAPI.js"]'
      );
      scripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleSpeechButtonClick = () => {
    // Speech to text functionality will be handled by webSpeechAPI.js
    if (window.speechRecognition) {
      // Trigger speech recognition
    }
  };

  // Helper function to recreate stream and reconnect
  const recreateStreamAndReconnect = async () => {
   
    
    // Clean up existing connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Show idle video when disconnecting
    if (streamVideoRef.current) {
      streamVideoRef.current.style.opacity = "0";
    }
    if (idleVideoRef.current) {
      idleVideoRef.current.style.opacity = "1";
    }
    
    streamIdRef.current = null;
    setCurrentStreamId(null);
    setConnectionStatus("disconnected");
    
    // Reconnect with new stream
    await handleConnect();
    
    // Wait for connection to stabilize
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    if (!streamIdRef.current) {
      throw new Error("Failed to recreate stream session");
    }
    
   
    return streamIdRef.current;
  };

  const handleActionButtonClick = async () => {
    const text = textAreaRef.current?.value?.trim();
    if (!text) {
      return;
    }

    // Add user message to chat history
    setChatHistory((prev) => [...prev, { role: "user", message: text, timestamp: new Date() }]);

    // Clear textarea
    if (textAreaRef.current) {
      textAreaRef.current.value = "";
    }

    const maxRetries = 2;
    let retryCount = 0;
    let shouldRetry = true;

    while (shouldRetry && retryCount <= maxRetries) {
      try {
        // If not connected, connect first
        if (!streamIdRef.current || connectionStatus !== "connected") {
          
          await handleConnect();
          
          // Wait a bit for connection to stabilize
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // Verify streamId exists before sending
        if (!streamIdRef.current) {
          throw new Error("Stream ID not available. Please connect first.");
        }

       
        
        // Send text message
        await sendTextContent(streamIdRef.current, text);
        
        
        // Success - break out of retry loop
        shouldRetry = false;
      } catch (error) {
        console.error("Error sending message:", error);
        const errorMessage = error.message || "Unknown error occurred";
        
        // Check if it's a stream session not found error
        const isStreamNotFoundError = 
          errorMessage.includes("Stream session not found") || 
          errorMessage.includes("Create agent stream first") ||
          errorMessage.toLowerCase().includes("stream session not found");
        
        if (isStreamNotFoundError && retryCount < maxRetries) {
          const currentRetry = retryCount + 1;
         try {
            // Recreate stream and reconnect
            await recreateStreamAndReconnect();
            
            // Add info message to chat
            setChatHistory((prev) => [
              ...prev,
              { 
                role: "system", 
                message: "Connection re-established. Retrying...", 
                timestamp: new Date() 
              },
            ]);
            
            retryCount = currentRetry;
            // Continue to retry the send
          } catch (reconnectError) {
            console.error("Failed to recreate stream:", reconnectError);
            setChatHistory((prev) => [
              ...prev,
              { 
                role: "error", 
                message: `Failed to reconnect: ${reconnectError.message}`, 
                timestamp: new Date() 
              },
            ]);
            shouldRetry = false;
          }
        } else {
          // Not a stream error or max retries reached
          setChatHistory((prev) => [
            ...prev,
            { 
              role: "error", 
              message: retryCount >= maxRetries 
                ? `Error after ${maxRetries} retries: ${errorMessage}` 
                : `Error: ${errorMessage}`, 
              timestamp: new Date() 
            },
          ]);
          shouldRetry = false;
        }
      }
    }
  };

  const handleInterruptButtonClick = () => {
    // Interrupt functionality will be handled by agents-streams-api.js
    setIsInterruptVisible(false);
  };

  const handleConnectButtonClick = async () => {
    if (hiddenRef.current) {
      hiddenRef.current.style.display = "none";
    }
    await handleConnect();
  };

  // WebRTC Connection Handler
  const handleConnect = async () => {
    if (isConnectingRef.current) {
    
      return;
    }
    
    // If already connected with valid streamId, don't reconnect
    if (connectionStatus === "connected" && streamIdRef.current) {
      
      return;
    }
    
    // Clean up any existing connection
    if (peerConnectionRef.current) {
      
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Show idle video when starting new connection
    if (streamVideoRef.current) {
      streamVideoRef.current.style.opacity = "0";
    }
    if (idleVideoRef.current) {
      idleVideoRef.current.style.opacity = "1";
    }
    
    // Reset streamId if reconnecting
    if (connectionStatus === "disconnected") {
      streamIdRef.current = null;
      setCurrentStreamId(null);
    }

    try {
      isConnectingRef.current = true;
      setConnectionStatus("connecting");
      if (connectionLabelRef.current) {
        connectionLabelRef.current.textContent = "Connecting...";
      }

      // 1. Create agent stream
     
      const streamData = await createAgentStream();
      
      if (!streamData || !streamData.id) {
        throw new Error("Failed to create stream: Invalid response from server");
      }
      
      streamIdRef.current = streamData.id;
      setCurrentStreamId(streamData.id);
     

      // 2. Create RTCPeerConnection
      const configuration = {
        iceServers: streamData.ice_servers.length > 0 
          ? streamData.ice_servers 
          : [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:stun1.l.google.com:19302" },
            ],
      };

      const pc = new RTCPeerConnection(configuration);
      peerConnectionRef.current = pc;

      // Handle incoming video stream
      pc.ontrack = (event) => {
       
        if (event.streams && event.streams[0]) {
          const stream = event.streams[0];
          if (streamVideoRef.current) {
            streamVideoRef.current.srcObject = stream;
            streamVideoRef.current.style.opacity = "1";
            if (idleVideoRef.current) {
              idleVideoRef.current.style.opacity = "0";
            }
          }
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = async (event) => {
        if (event.candidate && streamIdRef.current) {
          try {
         
          } catch (error) {
            console.warn("Error submitting ICE candidate:", error);
            // Don't throw - ICE candidate failures are often non-critical
          }
        } else if (event.candidate && !streamIdRef.current) {
          console.warn("ICE candidate received but streamId is missing");
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        
        setConnectionStatus(state === "connected" ? "connected" : "disconnected");
        
        // Show idle video when disconnected, hide when connected
        if (state === "connected" || state === "connecting") {
          // Stream video will be shown when track arrives
          if (idleVideoRef.current) {
            idleVideoRef.current.style.opacity = "0";
          }
        } else {
          // Show idle video when disconnected
          if (streamVideoRef.current) {
            streamVideoRef.current.style.opacity = "0";
          }
          if (idleVideoRef.current) {
            idleVideoRef.current.style.opacity = "1";
          }
        }
        
        if (connectionLabelRef.current) {
          if (state === "connected") {
            connectionLabelRef.current.textContent = "Connected";
            connectionLabelRef.current.style.color = "green";
          } else if (state === "connecting") {
            connectionLabelRef.current.textContent = "Connecting...";
            connectionLabelRef.current.style.color = "orange";
          } else {
            connectionLabelRef.current.textContent = "Disconnected";
            connectionLabelRef.current.style.color = "red";
          }
        }
      };

      // 3. Set remote description (offer from server)
      await pc.setRemoteDescription(new RTCSessionDescription(streamData.offer));

      // 4. Create and set local description (answer)
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // 5. Submit SDP answer to server
      if (!streamIdRef.current) {
        throw new Error("Stream ID lost during connection setup");
      }
      
      
      await submitSdpAnswer(streamIdRef.current, {
        type: answer.type,
        sdp: answer.sdp,
      });
      

      setConnectionStatus("connected");
      if (connectionLabelRef.current) {
        connectionLabelRef.current.textContent = "Connected";
        connectionLabelRef.current.style.color = "green";
      }
    } catch (error) {
      console.error("Error connecting:", error);
      setConnectionStatus("disconnected");
      if (connectionLabelRef.current) {
        connectionLabelRef.current.textContent = `Error: ${error.message}`;
        connectionLabelRef.current.style.color = "red";
      }
    } finally {
      isConnectingRef.current = false;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
    };
  }, []);

  return (
    // Scopes this demo's stylesheet so its bare `header` rules cannot leak
    // onto the storefront's pages.
    <div className="did-demo">
      <header>
        <h1>
          <img
            src="https://studio.d-id.com/assets/did_logo_dark-17fd213e.svg"
            alt="D-ID Logo"
            className="didImage"
          />{" "}
          Agents 2.0 API Demo
        </h1>
      </header>
      <div id="container" style={{ display: "flex" }}>
        <div className="header">
          <span id="previewName" ref={previewNameRef}>
            Your Agent
          </span>
          <span id="connectionLabel" ref={connectionLabelRef}></span>
          {currentStreamId && (
            <span style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.25rem" }}>
              Stream ID: {currentStreamId.substring(0, 20)}...
            </span>
          )}
          {connectionStatus !== "connected" && (
            <button
              onClick={handleConnect}
              disabled={connectionStatus === "connecting"}
              style={{
                marginTop: "0.5rem",
                padding: "0.5rem 1rem",
                backgroundColor: connectionStatus === "connecting" ? "#ccc" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: connectionStatus === "connecting" ? "not-allowed" : "pointer",
                fontSize: "0.875rem",
              }}
            >
              {connectionStatus === "connecting" ? "Connecting..." : "Connect"}
            </button>
          )}
        </div>
        <div id="video-wrapper">
          <video
            id="streamVideoElement"
            ref={streamVideoRef}
            autoPlay
            loop
            playsInline
            style={{ opacity: 0 }}
          ></video>
          <video
            id="idleVideoElement"
            ref={idleVideoRef}
            src={IdleVideo}
            autoPlay
            loop
            playsInline
            muted
            style={{ opacity: 1 }}
          ></video>
        </div>
        <div id="buttons">
          <input
            type="radio"
            id="chatOption"
            name="option"
            value="chat"
            checked={selectedOption === "chat"}
            onChange={handleOptionChange}
          />
          <label
            htmlFor="chatOption"
            className="button"
            title="Chat Endpoint -> Communicate with your Agent (D-ID's LLM Response)"
          >
            Chat
          </label>
          <input
            type="radio"
            id="speakOption"
            name="option"
            value="speak"
            checked={selectedOption === "speak"}
            onChange={handleOptionChange}
          />
          <label
            htmlFor="speakOption"
            className="button"
            title="Speak Endpoint -> Streaming API (Repeats the input)"
          >
            Speak
          </label>
        </div>
        <div className="inputsDiv">
          <div className="mainInput">
            <textarea
              id="textArea"
              ref={textAreaRef}
              placeholder="Write something — 'Chat' replies, 'Speak' repeats."
              autoFocus
            ></textarea>
            <button
              id="speechButton"
              className="roundButton"
              title="Speech to Text - Web Speech API (MDN)"
              onClick={handleSpeechButtonClick}
            ></button>
            <button
              id="actionButton"
              className="roundButton"
              title="Send Message"
              onClick={handleActionButtonClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="37"
                viewBox="0 0 26 27"
                fill="none"
              >
                <path
                  d="M10.8753 20.9288L15.8809 18.3172C20.2537 16.0357 22.4402 14.895 22.4402 13.0885C22.4402 11.2821 20.2537 10.1413 15.8809 7.85984L10.8753 5.24821C7.34598 3.40683 5.58133 2.48614 4.5583 2.76876C3.58508 3.03761 2.82497 3.83076 2.56732 4.84629C2.29649 5.91381 3.17881 7.75518 4.94346 11.4379C5.16215 11.8943 5.60928 12.198 6.09829 12.2003L14.0891 12.2391C14.5386 12.2413 14.9014 12.6234 14.8993 13.0925C14.8972 13.5616 14.531 13.9401 14.0815 13.9379L6.21975 13.8997C5.68076 13.8971 5.1845 14.2361 4.94346 14.7391C3.17882 18.4218 2.29648 20.2632 2.56732 21.3307C2.82497 22.3463 3.58507 23.1394 4.5583 23.4083C5.58133 23.6909 7.34599 22.7702 10.8753 20.9288Z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              id="interruptButton"
              className="roundButton"
              title="Interrupt"
              style={{
                display: isInterruptVisible ? "block" : "none",
                backgroundColor: "black",
              }}
              onClick={handleInterruptButtonClick}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12Z"
                  stroke="currentcolor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          </div>
        </div>
        <details id="answers" ref={answersRef}>
          <summary>
            {" "}
            Chat History ({chatHistory.length}){" "}
            <svg
              style={{ verticalAlign: "text-top" }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
            >
              <path
                fill="currentcolor"
                d="m13.087 21.388.645.382-.645-.382Zm.542-.916-.646-.382.646.382Zm-3.258 0-.645.382.645-.382Zm.542.916.646-.382-.646.382Zm-8.532-5.475.693-.287-.693.287Zm5.409 3.078-.013.75.013-.75Zm-2.703-.372-.287.693.287-.693Zm16.532-2.706.693.287-.693-.287Zm-5.409 3.078-.012-.75.012.75Zm2.703-.372.287.693-.287-.693Zm.7-15.882-.392.64.392-.64Zm1.65 1.65.64-.391-.64.392ZM4.388 2.738l-.392-.64.392.64Zm-1.651 1.65-.64-.391.64.392ZM9.403 19.21l.377-.649-.377.649Zm4.33 2.56.541-.916-1.29-.764-.543.916 1.291.764Zm-4.007-.916.542.916 1.29-.764-.541-.916-1.291.764Zm2.715.152a.52.52 0 0 1-.882 0l-1.291.764c.773 1.307 2.69 1.307 3.464 0l-1.29-.764ZM10.5 2.75h3v-1.5h-3v1.5Zm10.75 7.75v1h1.5v-1h-1.5Zm-18.5 1v-1h-1.5v1h1.5Zm-1.5 0c0 1.155 0 2.058.05 2.787.05.735.153 1.347.388 1.913l1.386-.574c-.147-.352-.233-.782-.278-1.441-.046-.666-.046-1.51-.046-2.685h-1.5Zm6.553 6.742c-1.256-.022-1.914-.102-2.43-.316L4.8 19.313c.805.334 1.721.408 2.977.43l.026-1.5ZM1.688 16.2A5.75 5.75 0 0 0 4.8 19.312l.574-1.386a4.25 4.25 0 0 1-2.3-2.3l-1.386.574Zm19.562-4.7c0 1.175 0 2.019-.046 2.685-.045.659-.131 1.089-.277 1.441l1.385.574c.235-.566.338-1.178.389-1.913.05-.729.049-1.632.049-2.787h-1.5Zm-5.027 8.241c1.256-.021 2.172-.095 2.977-.429l-.574-1.386c-.515.214-1.173.294-2.428.316l.025 1.5Zm4.704-4.115a4.25 4.25 0 0 1-2.3 2.3l.573 1.386a5.75 5.75 0 0 0 3.112-3.112l-1.386-.574ZM13.5 2.75c1.651 0 2.837 0 3.762.089.914.087 1.495.253 1.959.537l.783-1.279c-.739-.452-1.577-.654-2.6-.752-1.012-.096-2.282-.095-3.904-.095v1.5Zm9.25 7.75c0-1.622 0-2.891-.096-3.904-.097-1.023-.299-1.862-.751-2.6l-1.28.783c.285.464.451 1.045.538 1.96.088.924.089 2.11.089 3.761h1.5Zm-3.53-7.124a4.25 4.25 0 0 1 1.404 1.403l1.279-.783a5.75 5.75 0 0 0-1.899-1.899l-.783 1.28ZM10.5 1.25c-1.622 0-2.891 0-3.904.095-1.023.098-1.862.3-2.6.752l.783 1.28c.464-.285 1.045-.451 1.96-.538.924-.088 2.11-.089 3.761-.089v-1.5ZM2.75 10.5c0-1.651 0-2.837.089-3.762.087-.914.253-1.495.537-1.959l-1.279-.783c-.452.738-.654 1.577-.752 2.6C1.25 7.61 1.25 8.878 1.25 10.5h1.5Zm1.246-8.403a5.75 5.75 0 0 0-1.899 1.899l1.28.783a4.25 4.25 0 0 1 1.402-1.403l-.783-1.279Zm7.02 17.993c-.202-.343-.38-.646-.554-.884a2.229 2.229 0 0 0-.682-.645l-.754 1.297c.047.028.112.078.224.232.121.166.258.396.476.764l1.29-.764Zm-3.24-.349c.44.008.718.014.93.037.198.022.275.054.32.08l.754-1.297c-.293-.17-.598-.24-.909-.274-.298-.033-.657-.038-1.069-.045l-.025 1.5Zm6.498 1.113c.218-.367.355-.598.476-.764.112-.154.177-.204.224-.232l-.754-1.297c-.29.17-.5.395-.682.645-.173.238-.352.54-.555.884l1.291.764Zm1.924-2.612c-.412.007-.771.012-1.069.045-.311.035-.616.104-.909.274l.754 1.297c.045-.026.122-.058.32-.08.212-.023.49-.03.93-.037l-.026-1.5Z"
              />
              <path
                stroke="currentcolor"
                strokeLinecap="round"
                strokeWidth="1.5"
                d="M8 9h8M8 12.5h5.5"
              />
            </svg>
          </summary>
          <div style={{ padding: "1rem", maxHeight: "400px", overflowY: "auto" }}>
            {chatHistory.length === 0 ? (
              <p style={{ color: "#666", fontStyle: "italic" }}>No messages yet. Start a conversation!</p>
            ) : (
              chatHistory.map((item, index) => {
                const getRoleStyle = () => {
                  switch (item.role) {
                    case "user":
                      return { bg: "#e3f2fd", border: "#2196f3", label: "You" };
                    case "error":
                      return { bg: "#ffebee", border: "#f44336", label: "Error" };
                    case "system":
                      return { bg: "#fff3e0", border: "#ff9800", label: "System" };
                    default:
                      return { bg: "#f5f5f5", border: "#9e9e9e", label: "Agent" };
                  }
                };
                const roleStyle = getRoleStyle();
                
                return (
                  <div
                    key={index}
                    style={{
                      marginBottom: "1rem",
                      padding: "0.75rem",
                      borderRadius: "8px",
                      backgroundColor: roleStyle.bg,
                      borderLeft: `4px solid ${roleStyle.border}`,
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                      {roleStyle.label}
                    </div>
                    <div>{item.message}</div>
                    <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.25rem" }}>
                      {item.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </details>
      </div>
      <div id="hidden" ref={hiddenRef} style={{ display: "none" }}>
        <h2 id="hidden_h2" ref={hiddenH2Ref}>Session Status</h2>
        <br />
        <h3>Want to continue where we left off?</h3>
        <br />
        <button
          id="connectButton"
          className="button"
          title="Connect to a new WebRTC Session"
          ref={connectButtonRef}
          onClick={handleConnectButtonClick}
        >
          Let's continue
        </button>
      </div>
    </div>
  );
}
